import {
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useLatest } from 'react-use'
import { loadCaseLabels, naturalSortComparator } from '@editorUtils'
import produce from 'immer'
import {
  findIndex,
  isArray,
  isNaN,
  isUndefined,
  last,
  reduce,
  sortBy,
  toNumber,
  round,
} from 'lodash-es'
import { Warning } from '@mui/icons-material'
import AnchorIcon from '@mui/icons-material/Anchor'
import {
  Stack,
  Card,
  CardContent,
  Popover,
  Typography,
  Alert,
  CardActions,
  Button,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material'
import {
  GridCellParams,
  GridCellSelectionModel,
  GridColDef,
  GridProSlotsComponent,
  GridRenderEditCellParams,
  GridRowsProp,
  GridTreeNode,
  GridTreeNodeWithRender,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import useCornerConnectionAdditionalLoadProposalState from '@editor/pages/Results2/hooks/useCornerConnectionAdditionalLoadProposalState'
import { UtilizationTextIcon } from '@ui/icons/misc'
import { useSnackbar } from '@ui/snackbar'
import { useElementLabel } from '@editorHooks'
import { formulaToNumber } from '@utils'
import {
  getCellClassName,
  gridColumnGroupingModel,
  initialState,
  loadSources,
  slotProps,
} from '../../constants'
import StyledDataGrid from '../StyledDataGrid'
import AnchorDataGridToolbar from './components/AnchorDataGridToolbar'
import { fieldToHeaderName } from './constants'
import useSegmentLabel from './hooks/useSegmentLabel'
import { createManualLoadField, quickFilterDisabledFn } from './misc'

const snackbarWarningKey = 'fix-or-reset-anchor-grid-snackbar'

interface Ref {
  selectCell: (anchorGuid: string, field: string) => void

  updateCornerConnectionState: (
    anchorGuid: string,
    manualLoadField: string,
    manualSourceField: string,
    rawFormula: string,
    maxForce: number,
  ) => void
}

export type { Ref as AnchorDataGrid2Ref }

interface Props {
  disallowSelection?: boolean
  onClose: () => void
  onSave: () => void
  disableAllExcept?: { anchorGuid: string; fields: string[] }
  selectedConnector?: string | null
  onSelectConnector: (connector?: string) => void
}

const AnchorDataGridTable = forwardRef<Ref, Props>(function AnchorDataGrid2(
  { disallowSelection, onClose, onSave, disableAllExcept, selectedConnector, onSelectConnector },
  ref,
): ReactElement {
  const getLabel = useElementLabel()

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { selectAnchor } = useCornerConnectionAdditionalLoadProposalState()

  const { watch, setValue, trigger, getValues } = useFormContext()

  const rows: GridRowsProp<AnchorInterventionsTableRowData> = watch('rows')

  const gridApiRef = useGridApiRef()

  useImperativeHandle(
    ref,
    () => ({
      selectCell: (anchorGuid: string, field: string) => {
        gridApiRef.current?.setCellFocus(anchorGuid, field)
        gridApiRef.current?.setCellSelectionModel({
          [anchorGuid]: {
            [field]: true,
          },
        })
        const index = gridApiRef.current.getRowIndexRelativeToVisibleRows(anchorGuid)

        gridApiRef.current?.scrollToIndexes({
          rowIndex: index,
        })
        gridApiRef.current.scrollToIndexes({
          rowIndex: index,
        })
      },
      updateCornerConnectionState: async (
        anchorGuid: string,
        manualLoadField: string,
        manualSourceField: string,
        rawFormula: string,
        maxForce: number,
      ) => {
        const rows = getValues().rows as AnchorInterventionsTableRowData[]

        const commentField = 'comment'
        const comment = `Eckverschraubung maximale Last: ${maxForce} kN`

        const index = findIndex(rows, row => row.support_guid === anchorGuid)
        if (index === -1) return

        const currentRowData = rows[index]
        const newRowData = produce(draft => {
          // @ts-expect-error
          draft[manualLoadField] = `-MIN(${rawFormula}, ${maxForce})`

          // @ts-expect-error
          draft[manualSourceField] = loadSources.wallCorner

          const commentCellValue = draft[commentField]

          draft[commentField] =
            commentCellValue === '' || isUndefined(commentCellValue)
              ? comment
              : `${commentCellValue}; ${comment}`
        }, currentRowData)()

        // updateRows(index, newRowData)

        const updatedArray = produce(rows, draft => {
          const index = draft.findIndex(item => item.support_guid === newRowData.support_guid)
          if (index !== -1) {
            draft[index] = newRowData
          }
        })

        // @ts-expect-error
        const manualLoadBefore = currentRowData[manualLoadField]
        if (manualLoadBefore !== '' && !isUndefined(manualLoadBefore)) {
          enqueueSnackbar(
            `Der aktuelle Wert des Feldes wurde ersetzt. Ersetzter Wert: ${manualLoadBefore}`,
            {
              variant: 'warning',
            },
          )
        }
        setValue('rows', updatedArray, { shouldDirty: true, shouldValidate: true })
      },
    }),
    [enqueueSnackbar, getValues, gridApiRef, setValue],
  )

  const [anchorEl, setAnchorEl] = useState<{
    element: HTMLDivElement | null
    supportGuid: string
    field: `manual_load_${1 | 2 | 3}`
  } | null>(null)

  // MEMOs

  const sourceGuidToAnchors = useMemo(
    () =>
      reduce(
        rows,
        (col, el) => ({
          ...col,
          [el.source_guid]: isArray(col[el.source_guid])
            ? sortBy([...col[el.source_guid], el.support_guid])
            : [el.support_guid],
        }),
        {} as Record<string, string[]>,
      ),
    [rows],
  )

  const anchorNameToNumber = useMemo(
    () =>
      reduce(
        rows,
        (col, el) => ({
          ...col,
          [el.support_guid]: (findIndex(
            sourceGuidToAnchors[el.source_guid],
            name => name === el.support_guid,
          ) + 1) as number,
        }),
        {} as Record<string, number>,
      ),
    [rows, sourceGuidToAnchors],
  )

  // Using a ref here so the columns stay the same
  const anchorNameToNumberRef = useLatest(anchorNameToNumber)

  const renderManualSource = useCallback((field: string) => {
    const ManualSourceComp = (
      params: GridRenderEditCellParams<
        AnchorInterventionsTableRowData,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        any,
        GridTreeNodeWithRender
      >,
    ) => (
      <Select
        value={params.value}
        onChange={event => {
          params.api.setEditCellValue({
            id: params.id,
            value:
              event.target.value === loadSources.empty || event.target.value === ''
                ? undefined
                : event.target.value,
            field: field,
          })
          params.api.stopCellEditMode({
            id: params.id,
            field: field,
          })
        }}
        autoFocus
        open
        onClose={event => {
          params.api.stopCellEditMode({
            id: params.id,
            field: field,
          })

          if (
            params.field.startsWith('manual_source_') &&
            'innerText' in event.currentTarget &&
            event.currentTarget.innerText === loadSources.wallCorner
          ) {
            const element = params.api.getCellElement(params.id, params.field)

            setAnchorEl({
              element: element,
              supportGuid: params.row.support_guid,
              field: `manual_load_${last(params.field) as '1' | '2' | '3'}`,
            })
          }
        }}
        style={{ width: '100%', border: 'none' }}
        data-cy="manual-source-select"
      >
        {Object.keys(loadSources).map(key => (
          <MenuItem key={key} value={loadSources[key as LoadSourceType]}>
            {loadSources[key as LoadSourceType]}
          </MenuItem>
        ))}
      </Select>
    )

    return ManualSourceComp
  }, [])

  const createManualSourceField = useCallback(
    (sourceIndex: '1' | '2' | '3'): GridColDef<AnchorInterventionsTableRowData> => ({
      ...fieldToHeaderName[`manual_source_${sourceIndex}`],
      renderHeader: () => (
        <Typography sx={{ fontWeight: 'bold', fontSize: 12 }}>Herkunft</Typography>
      ),
      headerAlign: 'center',
      align: 'left',
      width: 110,
      editable: true,
      renderEditCell: renderManualSource(`manual_source_${sourceIndex}`),
      groupable: true,
    }),
    [renderManualSource],
  )

  const getSegmentLabel = useSegmentLabel()

  // COLUMN definition

  const columns: GridColDef<AnchorInterventionsTableRowData>[] = useMemo(
    () => [
      {
        ...fieldToHeaderName.element_guid,
        width: 70,
        renderCell: params => getLabel(params.value),
        valueGetter: value => getLabel(value),
        groupable: true,
      },
      {
        ...fieldToHeaderName.segment_guid,
        renderHeader: () => (
          <Tooltip title="Segment">
            <Typography sx={{ fontWeight: 'bold', fontSize: 12 }}>Seg</Typography>
          </Tooltip>
        ),
        headerAlign: 'center',
        renderCell: params => getSegmentLabel(params.value, params.row.element_guid),
        valueGetter: (value, row) => getSegmentLabel(value, row.element_guid),
        width: 45,
        minWidth: 45,
        groupable: true,
      },
      {
        ...fieldToHeaderName.support_guid,
        renderHeader: () => (
          <Tooltip title="Anker">
            <AnchorIcon fontSize="inherit" />
          </Tooltip>
        ),
        headerAlign: 'center',
        // using a ref here in order not to recreate the columns definition if
        // any row changes as this is discouraged in the MUI datagrid
        // documentation as this leads to a number of issues e.g. resetting the
        // column widths.
        renderCell: params => anchorNameToNumberRef.current[params.value]?.toString(),
        valueGetter: value => anchorNameToNumberRef.current[value]?.toString(),
        width: 35,
        minWidth: 25,
        align: 'center',
        groupable: true,
      },
      {
        ...fieldToHeaderName.original_design_force,
        renderHeader: () => (
          <Typography sx={{ fontWeight: 'bold', fontSize: 12 }}>
            F<sub>d</sub>
          </Typography>
        ),
        headerAlign: 'center',
        renderCell: params =>
          params.rowNode.type === 'leaf' ? (params.value / 1000).toFixed(2) : '',
        getApplyQuickFilterFn: quickFilterDisabledFn,
        type: 'number',
        width: 65,
        groupable: false,
      },
      {
        ...fieldToHeaderName.original_design_force_source,
        sortComparator: naturalSortComparator,
        renderHeader: () => {
          return (
            <Tooltip title="maßgebender Lastfall">
              <div>
                <Typography sx={{ fontWeight: 'bold', fontSize: 12 }}>Lastfall</Typography>
              </div>
            </Tooltip>
          )
        },
        valueGetter: (value, row: AnchorInterventionsTableRowData) =>
          // @ts-expect-error
          loadCaseLabels[row.original_design_force_source_category]?.[value]?.short || 'n/a',
        renderCell: params => (params.rowNode.type === 'leaf' ? params.value : ''),
        width: 65,
        groupable: false,
      },
      createManualLoadField('manual_load_1'),
      createManualSourceField('1'),
      createManualLoadField('manual_load_2'),
      createManualSourceField('2'),
      createManualLoadField('manual_load_3'),
      createManualSourceField('3'),
      {
        ...fieldToHeaderName.load_sum,
        renderHeader: () => (
          <Typography sx={{ fontWeight: 'bold', fontSize: 12 }}>
            F<sub>d,neu</sub>
          </Typography>
        ),
        width: 65,
        renderCell: params => {
          if (params.rowNode.type !== 'leaf') return ''

          return params.value
        },
        valueGetter: (value, row: AnchorInterventionsTableRowData) => {
          const originalDesignForce = toNumber(row.original_design_force) / 1000

          try {
            const load1 = !isUndefined(row.manual_load_1) ? formulaToNumber(row.manual_load_1) : 0
            const load2 = !isUndefined(row.manual_load_2) ? formulaToNumber(row.manual_load_2) : 0
            const load3 = !isUndefined(row.manual_load_3) ? formulaToNumber(row.manual_load_3) : 0

            return (
              originalDesignForce +
              (isNaN(load1) ? 0 : round(load1, 2)) +
              (isNaN(load2) ? 0 : round(load2, 2)) +
              (isNaN(load3) ? 0 : round(load3, 2))
            ).toFixed(2)
          } catch (e) {
            return 'Fehler'
          }
        },
        type: 'number',
        groupable: false,
      },
      {
        ...fieldToHeaderName.anchor_utilization,
        headerAlign: 'center',
        renderHeader: () => (
          <Tooltip title="Ausnutzung">
            <UtilizationTextIcon />
          </Tooltip>
        ),
        valueGetter: (val: number) => {
          const num = toNumber(val)
          if (isNaN(num)) return undefined

          return num.toFixed(2)
        },
        type: 'number',
        width: 60,
        groupable: false,
        getApplyQuickFilterFn: quickFilterDisabledFn,
      },
      {
        ...fieldToHeaderName.anchor_name,
        flex: 1,
        width: 280,
        groupable: true,
      },
      {
        ...fieldToHeaderName.comment,
        editable: true,
        minWidth: 350,
        renderCell: params => (
          <Tooltip title={params.value} placement="left">
            {params.formattedValue}
          </Tooltip>
        ),
        flex: 1,
        groupable: true,
      },
    ],
    [anchorNameToNumberRef, createManualSourceField, getLabel, getSegmentLabel],
  )

  // Select the connector which is selected in the scene also in the table
  useEffect(() => {
    const selection = gridApiRef.current.getCellSelectionModel()

    if (selectedConnector && !Object.keys(selection).includes(selectedConnector)) {
      gridApiRef.current?.selectCellRange(
        { id: selectedConnector, field: 'element_guid' },
        { id: selectedConnector, field: 'comment' },
      )
      const index = gridApiRef.current.getRowIndexRelativeToVisibleRows(selectedConnector)

      try {
        gridApiRef.current?.scrollToIndexes({
          rowIndex: index,
        })
      } catch (e) {
        if (
          e instanceof TypeError &&
          e.message === "Cannot read properties of null (reading 'scrollTop')"
        ) {
          // do nothing
        } else throw e
      }
    }
  }, [gridApiRef, rows, selectedConnector])

  const processRowUpdate = useCallback(
    (newRow: GridValidRowModel) => {
      // React MUI Datagrid will sometimes set manual_source to an empty string
      // (e.g. when using delete button), this will fail validations. Hence we
      // need to clean it here. Unfortunately it was not possible to do that in
      // the "value_setter" method of the column
      const newRowCleaned = produce(newRow, draft => {
        if (draft.manual_source_1 === '') {
          draft.manual_source_1 = undefined
        }
        if (draft.manual_source_2 === '') {
          draft.manual_source_2 = undefined
        }
        if (draft.manual_source_3 === '') {
          draft.manual_source_3 = undefined
        }
      })

      const updatedArray = produce(rows, draft => {
        const index = draft.findIndex(item => item.support_guid === newRow.support_guid)
        if (index !== -1) {
          // @ts-expect-error
          draft[index] = newRowCleaned
        }
      })

      setValue('rows', updatedArray, { shouldDirty: true, shouldValidate: true })
      return newRowCleaned
    },
    [rows, setValue],
  )

  const onCellSelectionModelChange = useCallback(
    (selectionModel: GridCellSelectionModel) => {
      if (disallowSelection) return

      const keys = Object.keys(selectionModel)

      if (keys.length === 1 && !keys[0].startsWith('auto-generated-row')) {
        onSelectConnector(keys[0] as string)
      }
    },
    [onSelectConnector, disallowSelection],
  )

  const slots: Partial<GridProSlotsComponent> = useMemo(
    () => ({
      toolbar: () => (
        <AnchorDataGridToolbar
          anchorNameToNumber={anchorNameToNumber}
          onClose={async () => {
            const isValid = await trigger()
            if (!isValid) {
              enqueueSnackbar('Es sind noch Fehler in der Tabelle vorhanden', {
                key: snackbarWarningKey,
                persist: false,
                variant: 'info',
              })
              return
            }
            onClose()
            closeSnackbar(snackbarWarningKey)
          }}
          onSave={async () => {
            const isValid = await trigger()
            if (!isValid) {
              enqueueSnackbar('Es sind noch Fehler in der Tabelle vorhanden', {
                key: snackbarWarningKey,
                persist: false,
                variant: 'info',
              })
              return
            }

            onSave()
          }}
          onClickError={(rowId: string, field: string) => {
            // @ts-ignore
            ref.current.selectCell(rowId, field)
          }}
        />
      ),
    }),
    [anchorNameToNumber, closeSnackbar, enqueueSnackbar, onSave, onClose, ref, trigger],
  )

  const isCellEditable = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (params: GridCellParams<any, GridValidRowModel, GridValidRowModel, GridTreeNode>) => {
      if (!disallowSelection) return true

      if (isUndefined(disableAllExcept)) return true

      if (
        params.id === disableAllExcept.anchorGuid &&
        disableAllExcept.fields.includes(params.field)
      ) {
        return true
      }

      return false
    },
    [disableAllExcept, disallowSelection],
  )

  if (isUndefined(rows)) return <></>

  return (
    <>
      <StyledDataGrid
        disableSelectionOnClick={disallowSelection}
        apiRef={gridApiRef}
        rows={rows}
        // don't know why but after converting DataGrid to a styled component
        // there are typing issues for ref and columns, hence we use
        // ts-expect-error for now
        // @ts-expect-error
        columns={columns}
        density="compact"
        initialState={initialState}
        editMode="cell"
        rowHeight={35}
        columnHeaderHeight={40}
        showCellVerticalBorder
        onCellSelectionModelChange={onCellSelectionModelChange}
        getCellClassName={getCellClassName}
        processRowUpdate={processRowUpdate}
        showColumnVerticalBorder
        columnGroupingModel={gridColumnGroupingModel}
        slotProps={slotProps}
        slots={slots}
        hideFooter
        disableRowSelectionOnClick={disallowSelection}
        isCellEditable={isCellEditable}
        data-cy="anchor-table"
        cellSelection
        rowSelection={false}
        disableAggregation
      />

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl?.element}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Card sx={{ minWidth: 275 }}>
          <CardContent>
            <Stack direction="column" spacing={2}>
              <Typography variant="h5">Eingabehilfe für zusätzliche Lasten.</Typography>
              <Alert icon={<Warning fontSize="inherit" />} severity="warning">
                In dieser Zeit wird die Tabelle für Eingaben gesperrt.
              </Alert>
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                if (!anchorEl) return
                selectAnchor(anchorEl.supportGuid, anchorEl.field)
                setAnchorEl(null)
              }}
              variant="contained"
              data-cy="choose-corner-wall-btn"
            >
              Wand auswählen
            </Button>
            <Button onClick={() => setAnchorEl(null)}>Abbrechen</Button>
          </CardActions>
        </Card>
      </Popover>
    </>
  )
})

export default AnchorDataGridTable
